import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import withErrorHandling from "../HOC/ErrorHandling";

import { CopyBlock, dracula } from "react-code-blocks";
import {
  MoreHoriz,
  WarningAmber,
  ContentCopy,
  OpenInNew,
  CheckCircle,
  Error as ErrorIcon,
  Refresh,
  Edit,
  Delete,
  Code,
  History,
  Terminal,
} from "@mui/icons-material";

import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Chip,
  Paper,
  Divider,
  ListItemIcon,
  ListItemText,
} from "@mui/material";

import t from "../helpers/en";
import {
  EDIT_ITEM,
  JSON_ITEM,
  ROWS_PARAMS_LOGS,
  PAGE_SIZE,
  AUDIT_LOGS,
  FORCE_BUILD,
  DELETE_ITEM,
  BUILD_LOGS,
  FALSE,
} from "../helpers/constants";
import {
  getBuilds,
  getImages,
  getk8sLogs,
  forceRebuild,
  deleteApplication,
  restartService,
  getConfigurationData2Export,
  getAppByImageUid,
  getBuildLogs,
} from "../helpers/utils";
import { filterRecords, hasPermission } from "../helpers/selectors";
import usePopup from "../helpers/contexts/usePopup";

import { useSharedProps } from "../HOC/SharedProps";

import ConfigureBuild from "./ConfigureBuild";
import Table from "../components/Table/Table";
import Modal from "../components/Modal/Modal";
import PopupWindow from "../components/PopupWindow";

import { styleEllipsis, styleTableMenuOption } from "../GeneralStyles";
import Loader from "../components/Loading";

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.3, ease: "easeOut", delay: 0.1 },
  },
};

const staggerContainer = {
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0 },
};

// Status indicator component
const StatusBadge = ({ status, message }) => {
  const isHealthy = status !== "False";
  return (
    <Chip
      icon={isHealthy ? <CheckCircle sx={{ fontSize: "0.9rem" }} /> : <ErrorIcon sx={{ fontSize: "0.9rem" }} />}
      label={isHealthy ? "Healthy" : "Error"}
      size="small"
      sx={{
        background: isHealthy ? "rgba(34, 197, 94, 0.15)" : "rgba(239, 68, 68, 0.15)",
        color: isHealthy ? "#22c55e" : "#ef4444",
        fontWeight: 600,
        fontSize: "0.75rem",
        "& .MuiChip-icon": {
          color: isHealthy ? "#22c55e" : "#ef4444",
        },
      }}
    />
  );
};

// Info row component for the header card
const InfoRow = ({ label, value, hasCopy, hasUrl, onCopy, copied, index }) => (
  <motion.div variants={itemVariants}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 0.75,
        borderBottom: "1px solid #f1f5f9",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{
          color: "#64748b",
          fontSize: "0.75rem",
          fontWeight: 500,
          minWidth: "120px",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
        {hasUrl ? (
          <Typography
            component="a"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "#F97316",
              fontSize: "0.875rem",
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ...styleEllipsis,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {value}
            <OpenInNew sx={{ fontSize: "0.875rem" }} />
          </Typography>
        ) : (
          <Typography
            sx={{
              color: "#334155",
              fontSize: "0.875rem",
              fontWeight: 500,
              ...styleEllipsis,
            }}
          >
            {value}
          </Typography>
        )}
        {hasCopy && value && (
          <Tooltip title={copied ? "Copied!" : "Copy"} arrow>
            <IconButton
              size="small"
              onClick={onCopy}
              sx={{
                p: 0.5,
                color: copied ? "#22c55e" : "#94a3b8",
                "&:hover": { background: "rgba(234, 88, 12, 0.1)", color: "#F97316" },
              }}
            >
              {copied ? (
                <CheckCircle sx={{ fontSize: "1rem" }} />
              ) : (
                <ContentCopy sx={{ fontSize: "1rem" }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  </motion.div>
);

const WarningSign = ({ name, statusMsg }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <span>{name}</span>
    {statusMsg && (
      <Tooltip title={statusMsg} arrow>
        <WarningAmber sx={{ fill: "#ff0000 !important" }} fontSize="small" />
      </Tooltip>
    )}
  </Box>
);

const rowsParams = (data) => ({
  header: [t.name, t.buildNum, t.latestBuildReason, t.latestImage, t.createdOn],
  body: data,
  bodyCount: data,
  bodyParams: (val) => [
    {
      value: (
        <WarningSign
          name={val?.metadata?.displayName ?? val?.metadata?.name}
          statusMsg={
            val?.status?.conditions?.[0]?.status === FALSE &&
            (val?.status?.conditions?.[0]?.message || "-")
          }
        />
      ),
    },
    { value: val?.metadata?.labels?.["image.kpack.io/buildNumber"] },
    { value: val?.metadata?.annotations?.["image.kpack.io/reason"] },
    {
      value: val?.status?.latestImage,
      hasToolip: true,
      style: { maxWidth: "10em" },
      specialDetails: {
        hasTooltip: true,
        ellipsis: { maxWidth: "10em", ...styleEllipsis },
      },
    },
    {
      value: new Date(val?.metadata?.creationTimestamp).toLocaleString("en-US"),
    },
  ],
});

const ImageDetails = (props) => {
  const { showError, currentUser } = props;

  const [data, setData] = useState({
    data: [],
    filtered: [],
  });
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false); // Tracks if the discard modal is open
  const [hasChanges, setHasChanges] = useState(false); // Tracks if content has been modified
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [pressedBtn, setPressedBtn] = useState(null);
  const [copied, setCopied] = useState({});
  const [anchorEl, setAnchorEl] = useState({});

  const { id } = useParams();
  const { currentGroup } = useSharedProps();
  const {
    buttonRef,
    popupRef,
    popupPosition,
    openPopup,
    closePopup,
    onMouseLeave,
    openPopupForItem,
  } = usePopup(80);
  const navigate = useNavigate();

  const fetchDataFromAPI = async (callback, param) => {
    return new Promise((resolve, reject) => {
      callback(param, (result, err) => {
        if (err) reject(err);
        else resolve(result);
      });
    });
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        const [data1, data2] = await Promise.all([
          fetchDataFromAPI(getBuilds, { imageName: id }),
          fetchDataFromAPI(getImages, { name: id }),
        ]);

        setData({
          data: data1,
          filtered: data1,
          parent: data2,
        });

        setIsLoading(false);
      } catch (err) {
        showError(err);
      }
    };

    fetchData();
  }, [showError, id]);

  const onRestart = () => {
    let name = data?.parent?.metadata?.displayName?.replace("-image", "");

    restartService(name, (result, err) => {
      if (err) {
        showError(err);
      }
    });

    return;
  };

  const onChangeRowsPerPage = (evt) => {
    setRowsPerPage(parseInt(evt.target.value, 10));
    setPage(0);
  };

  const onChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const onFilterRecords = (result, currentFilters) => {
    const res = filterRecords(result, currentFilters, (item) => [
      item.metadata?.displayName,
    ]);

    setData(res);
  };

  const onKeyPressSearch = (evt) => {
    evt.preventDefault();

    if (evt.key === "Enter") {
      onFilterRecords(data?.data, filters);
      setPage(0);
    }
  };

  const onChangeSearchTxt = (evt) => {
    const { value } = evt.target;

    setFilters({ ...filters, searchedValue: value });
  };

  const onClearSearchTxt = () => {
    const copyFilters = { ...filters, searchedValue: null };

    setFilters(copyFilters);
    onFilterRecords(data?.data, copyFilters);
  };

  const onOpen = (evt, row) => {
    const targetId = evt.target.id;
    let modalData = {};

    if (targetId === AUDIT_LOGS) {
      new Promise((resolve) => {
        const params = {
          objectName: row?.metadata?.name,
          pageNumber: 0,
          pageSize: PAGE_SIZE,
        };

        getk8sLogs(params, (result) => {
          resolve({ ...row, logs: [...(result.content ?? [])] });
        });
      });
    } else if (targetId === BUILD_LOGS) {
      const params = {
        podName: row?.status?.podName,
      };

      getBuildLogs(params, async (result, err) => {
        if (err) {
          showError(err);
        } else {
          setForm({ logs: result });
        }
      });
    } else if (targetId === FORCE_BUILD) {
      forceRebuild(row?.metadata, async (result, err) => {
        if (err) {
          showError(err);
        } else {
          const dataBuilds = await fetchDataFromAPI(getBuilds, {
            imageName: id,
          });

          setData((prev) => ({
            ...prev,
            data: dataBuilds,
            filtered: dataBuilds,
          }));
        }
      });
      return;
    } else if (targetId === EDIT_ITEM) {
      setForm((prev) => ({ ...prev, isLoading: true }));

      const imageUid = data?.parent?.metadata?.uid;

      getAppByImageUid(imageUid, (result, error) => {
        if (!error) {
          setForm({ isEditForm: true, ...result, isLoading: false });
        } else {
          setForm((prev) => ({ ...prev, isLoading: false }));
        }
      });
    } else {
      modalData = { ...(row ? { ...row } : {}) };

      setForm(modalData);
    }

    setPressedBtn(targetId);
    setOpen(true);
  };

  const handleClose = (shouldDiscard) => {
    if (shouldDiscard && hasChanges) {
      setIsDiscardOpen(true);
    } else {
      setOpen(false);
      setForm({});
      setIsDiscardOpen(false);
      setHasChanges(false);
    }
  };

  const copy2Clipboard = async (el) => {
    try {
      await navigator.clipboard.writeText(el.value);
      setCopied((prevState) => ({
        ...prevState,
        ...el,
        status: true,
      }));
    } catch {}
  };

  const onToggleMenuItem = (event, id) => {
    setAnchorEl((prev) => ({
      ...prev,
      [id]: !event ? null : event.currentTarget,
    }));
  };

  const onExportConfigurationData = () => {
    getConfigurationData2Export(
      { uid: data?.parent?.metadata?.uid },
      (result, err) => {
        if (!err) {
          const blob = new Blob([JSON.stringify(result, null, 2)], {
            type: "application/json",
          });

          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = "";

          link.click();
        }
      }
    );
  };

  const menuMore = [
    ...(hasPermission(currentUser, ["editApplications"])
      ? [{ label: t.update, id: EDIT_ITEM }]
      : []),
    { label: t.json, id: JSON_ITEM },
    { label: "Export Configuration Data", onClick: onExportConfigurationData },
    {
      label: "Force Rebuild",

      disabled: currentGroup?.id && !currentGroup?.isContributor2Group,
      id: FORCE_BUILD,
    },
    { label: "Restart", onClick: onRestart },
    { label: `${t.view} ${t.events}`, id: AUDIT_LOGS },
    {
      label: "Delete",
      id: DELETE_ITEM,
      disabled: currentGroup?.id && !currentGroup?.isContributor2Group,
    },
  ];

  const onDelete = () => {
    if (form?.isSubmitting) return;

    setForm((prev) => ({ ...prev, isSubmitting: true })); // Disable save button, to prevent multiple requests

    deleteApplication(data?.parent?.metadata?.uid, (result, err) => {
      if (!err) {
        navigate("/");
      } else {
        setForm((prev) => ({ ...prev, isSubmitting: false }));
        showError(err);
      }
    });
  };

  const triggerUpdateTable = () => {
    handleClose();
  };

  const onDiscardClose = (shouldDiscard) => {
    if (shouldDiscard) {
      // Discard changes and close the modals
      handleClose();
    } else {
      setIsDiscardOpen(false); // Close discard modal only
    }
  };

  const onDetectFormChange = () => {
    setHasChanges(true); // Mark form as edited, on attempt to discard will popup dicard form
  };

  let title, content, saveLabel, onConfirmModalAction;

  switch (pressedBtn) {
    case JSON_ITEM:
      title = t.json;
      content = (
        <Box>
          <CopyBlock
            customStyle={{ height: "550px", width: "100%", overflow: "scroll" }}
            text={JSON.stringify(form, null, 4)}
            language={"en"}
            showLineNumbers={true}
            theme={dracula}
          />
        </Box>
      );
      break;
    case AUDIT_LOGS:
      title = t.events;
      content = (
        <Table
          rows={{
            ...ROWS_PARAMS_LOGS(form.logs ?? []),
            body: form?.logs ?? [],
          }}
          selectedRow={() => false}
          hideTableHeader={true}
        />
      );
      break;
    case EDIT_ITEM:
      title = t.edit(t.application);
      content = form?.isLoading ? (
        <Loader />
      ) : (
        <ConfigureBuild
          data={form}
          handleClose={() => handleClose(true)}
          {...{ triggerUpdateTable, currentUser, onDetectFormChange }}
        />
      );
      break;
    case DELETE_ITEM:
      title = t.delete(t.application);
      onConfirmModalAction = onDelete;
      saveLabel = t.confirm;
      break;
    case BUILD_LOGS:
      title = `${t.build} ${t.logs}`;
      content = (
        <Box>
          <CopyBlock
            customStyle={{ height: "550px", width: "100%", overflow: "scroll" }}
            text={form.logs}
            language={"text"}
            showLineNumbers={true}
            theme={dracula}
          />
        </Box>
      );
      break;
    default:
  }

  return (
    <Box>
      <Modal
        {...{ open }}
        handleClose={() => handleClose(pressedBtn === EDIT_ITEM)}
        closeLabel={t.close}
        title={title}
        onSave={onConfirmModalAction}
        {...{ content, saveLabel }}
        specificDetails={{
          ...(pressedBtn === EDIT_ITEM && {
            shouldHideFooter: true,
            style: {
              contentModal: { height: { xs: "100%", md: "50vh", lg: "70vh" } },
            },
          }),
          isSubmitting: form?.isSubmitting,
        }}
      />

      {/* Discard Modal */}
      <Modal
        title={t.discardChangesTitle}
        open={isDiscardOpen}
        content={t.discardChangesMsg}
        saveLabel={t.confirmDiscard}
        closeLabel={t.closeDiscard}
        handleClose={() => onDiscardClose(false)} // Cancel the discard
        onSave={() => onDiscardClose(true)} // Confirm discard
        specificDetails={{ style: { dialog: { maxWidth: "35em" } } }}
      />

      {/* Styled Actions Menu */}
      <Menu
        anchorEl={anchorEl[0]}
        open={Boolean(anchorEl[0])}
        onClose={() => onToggleMenuItem(null, 0)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
            border: "1px solid #e2e8f0",
            minWidth: "200px",
            overflow: "hidden",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {[
          { label: t.update, id: EDIT_ITEM, icon: <Edit fontSize="small" />, show: hasPermission(currentUser, ["editApplications"]) },
          { label: t.json, id: JSON_ITEM, icon: <Code fontSize="small" />, show: true },
          { label: "Export Config", onClick: onExportConfigurationData, icon: <Terminal fontSize="small" />, show: true },
          { label: "Force Rebuild", id: FORCE_BUILD, icon: <Refresh fontSize="small" />, disabled: currentGroup?.id && !currentGroup?.isContributor2Group, show: true },
          { label: "Restart", onClick: onRestart, icon: <Refresh fontSize="small" />, show: true },
          { label: `${t.view} ${t.events}`, id: AUDIT_LOGS, icon: <History fontSize="small" />, show: true },
          { divider: true, show: true },
          { label: "Delete", id: DELETE_ITEM, icon: <Delete fontSize="small" />, danger: true, disabled: currentGroup?.id && !currentGroup?.isContributor2Group, show: true },
        ]
          .filter((item) => item.show)
          .map((elem, elemIdx) =>
            elem.divider ? (
              <Divider key={elemIdx} sx={{ my: 0.5 }} />
            ) : (
              <MenuItem
                key={elemIdx}
                id={elem.id}
                disabled={elem.disabled}
                onClick={(evt) => {
                  onToggleMenuItem(null, 0);
                  elem?.onClick ? elem.onClick() : onOpen(evt, data?.parent);
                }}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: "0.875rem",
                  color: elem.danger ? "#ef4444" : "#334155",
                  "&:hover": {
                    background: elem.danger ? "#fef2f2" : "#f1f5f9",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>{elem.icon}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  {elem.label}
                </ListItemText>
              </MenuItem>
            )
          )}
      </Menu>

      {/* Modern Summary Card */}
      <Paper
        component={motion.div}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        sx={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          border: "1px solid #e2e8f0",
          mb: 3,
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: "#f1f5f9",
                fontWeight: 600,
                fontSize: "1.1rem",
              }}
            >
              {data?.parent?.metadata?.displayName || "Application Details"}
            </Typography>
            <StatusBadge
              status={data?.parent?.status?.conditions?.[0]?.status}
              message={data?.parent?.status?.conditions?.[0]?.message}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              label={`Build #${data?.parent?.metadata?.labels?.["image.kpack.io/buildNumber"] || "—"}`}
              size="small"
              sx={{
                background: "rgba(255,255,255,0.1)",
                color: "#94a3b8",
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
            <IconButton
              size="small"
              onClick={(evt) => onToggleMenuItem(evt, 0)}
              sx={{
                color: "#94a3b8",
                "&:hover": { background: "rgba(255,255,255,0.1)", color: "#f1f5f9" },
              }}
            >
              <MoreHoriz />
            </IconButton>
          </Box>
        </Box>

        {/* Card Body */}
        <Box
          component={motion.div}
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          sx={{ p: 3 }}
        >
          <InfoRow
            label="Latest Image"
            value={data?.parent?.status?.latestImage}
            hasCopy
            onCopy={() => copy2Clipboard({ value: data?.parent?.status?.latestImage, elIdx: 0 })}
            copied={copied.status && copied.elIdx === 0}
          />
          <InfoRow
            label="Source URL"
            value={data?.parent?.spec?.source?.git?.url}
            hasCopy
            hasUrl
            onCopy={() => copy2Clipboard({ value: data?.parent?.spec?.source?.git?.url, elIdx: 1 })}
            copied={copied.status && copied.elIdx === 1}
          />
          <InfoRow
            label="Created"
            value={data?.parent?.metadata?.creationTimestamp ? new Date(data?.parent?.metadata?.creationTimestamp).toLocaleString("en-US") : "—"}
          />
          {data?.parent?.status?.conditions?.[0]?.status === FALSE && (
            <motion.div variants={itemVariants}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mt: 2,
                  p: 2,
                  borderRadius: "8px",
                  background: "rgba(239, 68, 68, 0.08)",
                  border: "1px solid rgba(239, 68, 68, 0.2)",
                }}
              >
                <WarningAmber sx={{ color: "#ef4444", fontSize: "1.25rem", mt: 0.25 }} />
                <Box>
                  <Typography sx={{ color: "#ef4444", fontWeight: 600, fontSize: "0.875rem" }}>
                    Build Error
                  </Typography>
                  <Typography sx={{ color: "#dc2626", fontSize: "0.8rem", mt: 0.5 }}>
                    {data?.parent?.status?.conditions?.[0]?.message || "Unknown error occurred"}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          )}
        </Box>
      </Paper>
      <Table
        rows={{
          ...rowsParams(data?.filtered),
          body: data?.filtered?.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
          ),
        }}
        selectedRow={(item) => false}
        actionBtns={(item) => (
          <IconButton
            sx={{ padding: 0 }}
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation();

              openPopup(item, e.currentTarget);
            }}
          >
            <MoreHoriz sx={{ color: (theme) => theme.palette.primary.main }} />
          </IconButton>
        )}
        {...{ onChangeSearchTxt, onKeyPressSearch, onClearSearchTxt }}
        searchedValue={filters?.searchedValue}
        {...{
          page,
          rowsPerPage,
          onChangePage,
          onChangeRowsPerPage,
          isLoading,
        }}
      />

      {openPopupForItem && (
        <div ref={popupRef} onMouseEnter={() => {}} {...{ onMouseLeave }}>
          <PopupWindow
            specificDetails={{
              onClose: closePopup,
              popupPosition: popupPosition,
              style: { div: { width: "140px" } },
              actions: [
                {
                  id: JSON_ITEM,
                  label: t.json,
                  onClick: (evt) => onOpen(evt, openPopupForItem),
                },
                {
                  id: AUDIT_LOGS,
                  label: `${t.view} ${t.events}`,
                  onClick: (evt) => onOpen(evt, openPopupForItem),
                },
                {
                  id: BUILD_LOGS,
                  label: `${t.view} ${t.build} ${t.logs}`,
                  onClick: (evt) => onOpen(evt, openPopupForItem),
                },
              ]?.map((item, idx) => (
                <MenuItem
                  sx={{ ...styleTableMenuOption }}
                  key={idx}
                  id={item.id}
                  onClick={(evt) => {
                    if (item.onClick) {
                      item.onClick(evt);
                      closePopup();
                    }
                  }}
                  disabled={item.disabled}
                >
                  {item.label}
                </MenuItem>
              )),
            }}
          />
        </div>
      )}
    </Box>
  );
};

export default withErrorHandling(ImageDetails);
